import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import classes from "./GRN.module.css";
import {
  Box,
  Button,
  CircularProgress,
  Fade,
  Modal,
  TextField,
  Typography,
  Tabs,
  Tab,
} from "@mui/material";
import { useSelector } from "react-redux";
import axios from "axios";
import { API_ENDPOINT } from "../../../services/api/resources";
import SuccessPopupDashboard from "../../../components/Popups/PopupFrame/SuccessPopupDashboard";
import successPopupImg from "../../../assets/images/png/Group 2 2@2x.png";
import { useEffect } from "react";
import { CheckCircleOutline, HighlightOff } from "@mui/icons-material";

const GRNDetails = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const loginData = useSelector((state) => state.loginData.value);
  const role = loginData?.user?.designation;

  const [itemTab, setItemTab] = useState(0);
  const [remarks, setRemarks] = useState("");
  const [actionLoading, setActionLoading] = useState("");
  const [open, setOpen] = useState(false);

  /* =============================
     SPLIT ACTIVE & COMPLETED
  ============================== */
  const activeItems = state.items.filter(
    (i) => Number(i.quantity_ordered) !== Number(i.quantity_received),
  );

  const completedItems = state.items.filter(
    (i) => Number(i.quantity_ordered) === Number(i.quantity_received),
  );

  const itemsToShow = itemTab === 0 ? activeItems : completedItems;

  /* =============================
        APPROVAL LOGIC
  ============================== */

  const approvalStorageKey = `grn_approval_${state?.grn_number}_${loginData?.user?.token}`;

  const [localApprovals, setLocalApprovals] = useState(() => {
    const serverApprovals = state?.approvals || [];
    const persistedRaw = localStorage.getItem(approvalStorageKey);
    const persisted = persistedRaw ? JSON.parse(persistedRaw) : [];

    const merged = [...serverApprovals];
    persisted.forEach((item) => {
      if (!merged.some((a) => a.approval_level === item.approval_level)) {
        merged.push(item);
      }
    });
    return merged;
  });

  useEffect(() => {
    localStorage.setItem(approvalStorageKey, JSON.stringify(localApprovals));
  }, [localApprovals]);

  if (!state) return <p>No GRN Found</p>;

  const myApprovalLevel =
    role === "Store Executive" ? 1 : role === "Store Manager" ? 2 : null;

  const currentUserApproval = localApprovals.find(
    (a) => a.approval_level === myApprovalLevel,
  );

  const alreadyActionTaken = !!currentUserApproval;
  const currentUserStatus = currentUserApproval?.status;

  const anyRejected = localApprovals.some((a) => a.status === "rejected");

  const level1Approved = localApprovals.some(
    (a) => a.approval_level === 1 && a.status === "approved",
  );

  const level2Approved = localApprovals.some(
    (a) => a.approval_level === 2 && a.status === "approved",
  );

  const effectiveOverallStatus = anyRejected
    ? "rejected"
    : level1Approved && level2Approved
      ? "approved"
      : "pending";

  const canStoreExecutiveAct =
    role === "Store Executive" &&
    !alreadyActionTaken &&
    effectiveOverallStatus === "pending";

  const canStoreManagerAct =
    role === "Store Manager" &&
    level1Approved &&
    !alreadyActionTaken &&
    effectiveOverallStatus === "pending";

  const canTakeAction = canStoreExecutiveAct || canStoreManagerAct;
  const showWaitingForStoreExecutive =
    role === "Store Manager" &&
    !level1Approved &&
    !alreadyActionTaken &&
    effectiveOverallStatus === "pending";

  const isCommentValid = remarks.trim().length > 0;

  const approvalButtonSx = {
    minWidth: 132,
    minHeight: 44,
    px: 2.5,
    borderRadius: "10px",
    fontSize: "14px",
    fontFamily: "GilroyBold",
    letterSpacing: "normal",
    textTransform: "none",
    boxShadow: "none",
    lineHeight: 1.2,
    "& .MuiButton-startIcon": { display: "flex", mr: 1, ml: 0 },
    "&.Mui-focusVisible": { outline: "3px solid #93c5fd", outlineOffset: 3 },
    "&:hover": {
      boxShadow: "0 8px 18px rgba(50, 70, 113, 0.16)",
    },
    "&.Mui-disabled": {
      backgroundColor: "#f1f5f9",
      borderColor: "#e2e8f0",
      color: "#64748b",
    },
  };

  const handleApproval = async (status) => {
    if (!remarks.trim()) return;

    try {
      setActionLoading(status);

      await axios.post(API_ENDPOINT.approve_grn, {
        grn_number: state.grn_number,
        approver_token: loginData?.user?.token,
        status,
        remarks,
      });

      const updatedApproval = {
        approval_level: myApprovalLevel,
        status,
        approved_by: loginData?.user?.first_name,
      };

      setLocalApprovals((prev) => {
        const rest = prev.filter((a) => a.approval_level !== myApprovalLevel);
        return [...rest, updatedApproval];
      });

      setRemarks("");

      if (status === "approved" && role === "Store Manager") {
        setOpen(true);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setActionLoading("");
    }
  };

  /* =============================
          UI
  ============================== */

  return (
    <div className={classes.grncontainer}>
    <div style={{ display: "flex", justifyContent: "space-between", marginTop: "2rem" }}>

     <Typography variant="h4" sx={{fontFamily:"gilroySemibold"}}>GRN Details</Typography>

        <Box >
        <button
          onClick={() => navigate(-1)}
          style={{
            background: "#324671",
            color: "white",
            width: "100px",
            border: "none",
            padding: "10px",
            borderRadius: "5px",
            fontSize: "14px",
            fontWeight: 600,
          }}
        >
          Back
        </button>
      </Box>
    </div>

      {/* ================= TABS ================= */}
      <Box sx={{ mt: 3 }}>
        <Tabs value={itemTab} onChange={(e, v) => setItemTab(v)}>
          <Tab
            label={`Open GRN (${activeItems.length})`}
            sx={{
              fontSize: "14px",
              // textTransform: "none",
              fontFamily: "gilroyBold",
              letterSpacing: "normal",
              // fontWeight: "bold",
            }}
          />
          <Tab
            label={`Closed GRN (${completedItems.length})`}
            sx={{
              fontSize: "14px",
              // textTransform: "none",
              letterSpacing: "normal",
              // fontWeight: "bold",
              fontFamily: "gilroyBold",
            }}
          />
        </Tabs>
      </Box>

      {/* ================= TABLE ================= */}
      <table className={classes.potable}>
        <thead>
          <tr>
            <th style={{ fontFamily: "gilroyBold", fontSize: "14px" }}>
              Item name
            </th>
            <th style={{ fontFamily: "gilroyBold", fontSize: "14px" }}>
              Description
            </th>
            <th style={{ fontFamily: "gilroyBold", fontSize: "14px" }}>
              Ordered Quantity
            </th>
            <th style={{ fontFamily: "gilroyBold", fontSize: "14px" }}>
              Received Quantity
            </th>
            <th style={{ fontFamily: "gilroyBold", fontSize: "14px" }}>
              Unit Price
            </th>
            <th style={{ fontFamily: "gilroyBold", fontSize: "14px" }}>
              Total Price
            </th>
            <th style={{ fontFamily: "gilroyBold", fontSize: "14px" }}>
               Exception Remarks
            </th>
          </tr>
        </thead>

        <tbody>
          {itemsToShow.length > 0 ? (
            itemsToShow.map((item, idx) => (
              <tr key={idx}>
                <td>{item.item_name}</td>

                <td>{item.description}</td>
                <td>{item.quantity_ordered}</td>
                <td>{item.quantity_received}</td>
                <td>₹{Number(item.unit_price || 0).toLocaleString("en-IN")}</td>

                <td>
                  ₹{Number(item.total_price || 0).toLocaleString("en-IN")}
                </td>
                <td>{item.remarks}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="6" style={{ textAlign: "center" }}>
                No items available
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ================= APPROVAL SECTION ================= */}
      {canTakeAction && (
        <Box sx={{ mt: 3, p: { xs: 2, sm: 3 }, backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", boxShadow: "0 2px 8px #0f172a06" }}>
          <Typography id="grn-comments-label" component="h3" sx={{ mb: 0.75, fontSize: "16px", fontFamily: "GilroyBold", color: "#0f172a" }}>
            Comments
          </Typography>
          <Typography id="grn-comments-help" sx={{ mb: 2, fontSize: "14px", fontFamily: "GilroyMedium", color: "#64748b" }}>
            Add a comment before approving or rejecting this GRN.
          </Typography>

          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter your comments..."
            inputProps={{ "aria-labelledby": "grn-comments-label", "aria-describedby": "grn-comments-help", "aria-required": true }}
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#f8fafc",
                borderRadius: "10px",
                p: 2,
                "& fieldset": { borderColor: "#dbe3ec" },
                "&:hover fieldset": { borderColor: "#94a3b8" },
                "&.Mui-focused": { backgroundColor: "#ffffff", boxShadow: "0 0 0 3px #2563eb14" },
                "&.Mui-focused fieldset": { borderColor: "#2563eb" },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                fontFamily: "GilroyMedium",
                lineHeight: 1.6,
                color: "#0f172a",
                "&::placeholder": { color: "#64748b", opacity: 1 },
              },
            }}
          />
        </Box>
      )}

      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end", flexWrap: "wrap", gap: 1.5, "& > .MuiButton-root": { flex: { xs: "1 1 120px", sm: "0 0 auto" } } }}>
        {showWaitingForStoreExecutive && (
          <Box
            sx={{ width: "100%", display: "flex", justifyContent: "flex-end" }}
          >
            <Typography
              sx={{
                display: "inline-flex",
                alignItems: "center",
                minHeight: "40px",
                fontWeight: 600,
                fontSize: "14px",
                fontFamily: "GilroyBold",
                padding: "9px 16px",
                borderRadius: "999px",
                backgroundColor: "#fff7ed",
                color: "#b45309",
                border: "1px solid #fed7aa",
              }}
            >
              Waiting for Store Executive Approval
            </Typography>
          </Box>
        )}

        {canTakeAction && (
          <>
            <Button
              variant="contained"
              startIcon={actionLoading === "approved" ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutline />}
              sx={{
                ...approvalButtonSx,
                backgroundColor: "#15803d",
                "&:hover": {
                  backgroundColor: "#166534",
                  boxShadow: "0 8px 18px rgba(37, 111, 74, 0.18)",
                },
              }}
              disabled={!isCommentValid || !!actionLoading}
              onClick={() => handleApproval("approved")}
            >
              {actionLoading === "approved" ? "Processing..." : "Approve"}
            </Button>

            <Button
              variant="outlined"
              startIcon={actionLoading === "rejected" ? <CircularProgress size={18} color="inherit" /> : <HighlightOff />}
              sx={{
                ...approvalButtonSx,
                color: "#b42318",
                borderColor: "#fda29b",
                backgroundColor: "#fff5f4",
                "&:hover": {
                  backgroundColor: "#fee4e2",
                  borderColor: "#b42318",
                  boxShadow: "none",
                },
              }}
              disabled={!isCommentValid || !!actionLoading}
              onClick={() => handleApproval("rejected")}
            >
              {actionLoading === "rejected" ? "Processing..." : "Reject"}
            </Button>

            <Button
              variant="outlined"
              sx={{
                minWidth: 108,
                minHeight: 42,
                px: 2.5,
                borderRadius: "10px",
                fontSize: "14px",
                fontFamily: "GilroyBold",
                letterSpacing: "normal",
                textTransform: "none",
                borderColor: "#cbd5e1",
                color: "#324671",
                "&:hover": {
                  borderColor: "#324671",
                  backgroundColor: "#f8fafc",
                },
              }}
              onClick={() => navigate(-1)}
            >
              Cancel
            </Button>
          </>
        )}

        {alreadyActionTaken && (
          <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end" }}>
            <Typography
              sx={{
                display: "inline-flex",
                alignItems: "center",
                minHeight: "40px",
                fontWeight: 600,
                fontSize: "14px",
                fontFamily: "GilroyBold",
                padding: "9px 18px",
                borderRadius: "999px",
                backgroundColor:
                  currentUserStatus === "approved" ? "#ecfdf3" : "#fef3f2",
                color:
                  currentUserStatus === "approved" ? "#027a48" : "#b42318",
                border:
                  currentUserStatus === "approved"
                    ? "1px solid #abefc6"
                    : "1px solid #fecdca",
              }}
            >
              {currentUserStatus === "approved" ? "Approved" : "Rejected"}
            </Typography>
          </Box>
        )}
      </Box>

      {/* ================= SUCCESS MODAL ================= */}
      <Modal open={open}>
        <Fade in={open}>
          <Box>
            <SuccessPopupDashboard
              popupImage={successPopupImg}
              popupHeroText="Three Way and Asset Generated"
              popupDescription="Approved GRN should trigger 3-Way Matching and updating Asset Register."
              popBtn
              vendorBtn
              btnText="Okay"
              btnClick={() => {
                setOpen(false);
                navigate(-1);
              }}
            />
          </Box>
        </Fade>
      </Modal>
    </div>
  );
};

export default GRNDetails;
